import { useCallback, useEffect, useRef, useState } from 'react';
import notificationService from '../services/notificationService';
import auditService from '../services/auditService';
import { authService } from '../services/authService';
import { useModuleSettings } from '../contexts/ModuleSettingsContext';

const POLL_INTERVAL_MS = 600000;
const STORAGE_KEY = 'audit_notification_seen_ids';

const AUDIT_TYPES = ['kardex_audit', 'inventory_count'];

function loadSeenIds() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function saveSeenIds(set) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    // ignore
  }
}

export function useAuditNotifications() {
  const { kardexAuditModuleEnabled, loaded } = useModuleSettings();
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [unreadCount, setUnreadCount] = useState(0);
  const [isRecipient, setIsRecipient] = useState(false);
  const seenIdsRef = useRef(loadSeenIds());
  const pollingRef = useRef(null);

  const poll = useCallback(async () => {
    if (!authService.isAuthenticated() || !kardexAuditModuleEnabled) return;

    try {
      const me = await auditService.amIRecipient();
      const recipient = Boolean(me?.data?.is_recipient);
      setIsRecipient(recipient);
      if (!recipient) {
        setUnreadCount(0);
        return;
      }

      const countRes = await notificationService.unreadCount();
      setUnreadCount(Number(countRes?.count ?? countRes?.data?.count ?? 0));

      const response = await notificationService.list();
      const items = Array.isArray(response?.data) ? response.data : [];

      for (const item of items) {
        if (!item?.id || seenIdsRef.current.has(item.id)) continue;
        if (!AUDIT_TYPES.includes(item.type)) continue;
        if (item.read_at) {
          seenIdsRef.current.add(item.id);
          continue;
        }

        seenIdsRef.current.add(item.id);
        saveSeenIds(seenIdsRef.current);
        await notificationService.markAsRead(item.id).catch(() => {});

        setToast({
          isOpen: true,
          message: item.message || 'Nueva alerta de auditoría',
          type: item.toast_type || 'warning',
        });
        break;
      }
    } catch {
      // ignore polling errors
    }
  }, [kardexAuditModuleEnabled]);

  useEffect(() => {
    if (!loaded || !kardexAuditModuleEnabled) {
      setIsRecipient(false);
      setUnreadCount(0);
      return undefined;
    }

    poll();
    pollingRef.current = setInterval(poll, POLL_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loaded, kardexAuditModuleEnabled, poll]);

  return {
    toast,
    closeToast: () => setToast((t) => ({ ...t, isOpen: false })),
    unreadCount,
    isRecipient,
    refresh: poll,
  };
}

export default useAuditNotifications;
